import Button from '@/components/Button';
import Loading from '@/components/Loading';
import Modal from '@/components/Modal/modal';
import { getLazpadApi } from '@/gql';
import user from '@/stores/user';
import { TYPE } from '@/theme';
import storage from '@/utils/storage';
import { useState } from 'react';
import styled from 'styled-components';
import { useWalletClient } from 'wagmi';
// For EIP-712 structured signing, also import:
// import { useSignTypedData } from 'wagmi';

const Content = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px;
    gap: 24px;
    text-align: center;
    width: 400px;
    height: 300px;

    ${({ theme }) => theme.mediaWidth.upToSmall`
        width: 100%;
    `};
`;

const ButtonWrapper = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    margin-top: 16px;
`;

const StyledButton = styled(Button)`
    width: 100%;
    height: 48px;
    background-color: #000 !important;
    color: #fff !important;
`;

const LoadingWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 30px;
`;

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: (token: string, userId: string) => void;
}

export default function SignatureModal({ isOpen, onClose, onSuccess }: Props) {
    const [isLoading, setIsLoading] = useState(false);
    const [isRejected, setIsRejected] = useState(false);
    const { data: walletClient } = useWalletClient();

    const handleSign = async () => {
        if (!walletClient || !user.address) return;
        setIsLoading(true);
        setIsRejected(false);

        const api = getLazpadApi();
        try {
            // Generate signature message (using address and timestamp)
            const timestamp = Date.now();
            const serverNonce = await api.getNonce({
                address: user.address,
            });
            const message = `Sign this message to authenticate your wallet address \nNonce: ${
                serverNonce.getNonce?.data
            }\nAddress: ${user.address.toLowerCase()}`;

            // User signature
            const signature = await walletClient.signMessage({ message });

            const loginResponse = await api.login({
                req: {
                    ethAddress: user.address,
                    signature,
                    invitedCode: user.referrerCode,
                },
            });

            const tokenData = loginResponse?.login?.data;
            if (tokenData && tokenData.token && tokenData.userId) {
                const expiryTime = Date.now() + 7 * 24 * 60 * 60 * 1000; // 7 days expiry

                // Save token to localStorage with associated wallet address
                storage.set(`auth-token`, {
                    token: tokenData.token,
                    expiry: expiryTime,
                    address: user.address, // Save current connected wallet address
                    userId: tokenData.userId,
                });
                onSuccess(tokenData.token, tokenData.userId);
                setIsLoading(false);
                onClose();
            } else {
                console.error('Login failed, no token received');
                setIsLoading(false);
                setIsRejected(true);
            }
        } catch (error) {
            console.error('Signature or login failed:', error);
            setIsLoading(false);
            setIsRejected(true);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            disableBackgroundClick={isLoading}
            hideClose
        >
            <Content>
                {isLoading ? (
                    <LoadingWrapper>
                        <Loading size={80} />
                        <TYPE.main fontSize={16}>
                            Please sign in your wallet to continue
                        </TYPE.main>
                    </LoadingWrapper>
                ) : isRejected ? (
                    <>
                        <TYPE.main fontSize={20} fontWeight={600}>
                            Signature Rejected
                        </TYPE.main>
                        <TYPE.main fontSize={16}>
                            You need to sign the message to use all website
                            features. Please click the button below to try
                            again.
                        </TYPE.main>
                        <ButtonWrapper>
                            <StyledButton onClick={handleSign}>
                                Sign Again
                            </StyledButton>
                        </ButtonWrapper>
                    </>
                ) : (
                    <>
                        <TYPE.main fontSize={20} fontWeight={600}>
                            Please Sign
                        </TYPE.main>
                        <TYPE.main fontSize={16}>
                            To use our website features, please sign the message
                            in your wallet. This won't cost any gas fee.
                        </TYPE.main>
                        <ButtonWrapper>
                            <StyledButton onClick={handleSign}>
                                Sign
                            </StyledButton>
                        </ButtonWrapper>
                    </>
                )}
            </Content>
        </Modal>
    );
}
